import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import * as api from '../../lib/api';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import ButtonSpinner from '../../components/ui/ButtonSpinner';

const DISCOUNT_DESCRIPTIONS = {
  full: (v) => `100% off — Full access, completely free`,
  percent: (v) => `${v.value}% off your subscription`,
};

export const RedeemVoucher = () => {
  const { user } = useAuth();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const alreadyApplied = !!user?.applied_voucher;

  const handleApply = async () => {
    if (!code.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await api.redeemVoucher(user.id, code.trim());
      if (res.error === 'already_redeemed') {
        setResult({ type: 'error', message: 'You have already applied a voucher to your account.' });
      } else if (res.error) {
        setResult({ type: 'error', message: 'Invalid voucher code. Please check and try again.' });
      } else if (res.success) {
        setResult({ type: 'success', voucher: res.voucher });
        setCode('');
        window.location.reload();
      }
    } catch (err) {
      console.error(err);
      setResult({ type: 'error', message: err.message || 'Something went wrong. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6">
      <div className="flex items-center gap-3 mb-5">
        <div className="p-2 bg-amber-50 rounded-lg">
          <SafeIcon icon={FiIcons.FiGift} className="text-amber-600 text-lg" />
        </div>
        <div>
          <h2 className="font-semibold text-brand-navy">Redeem Voucher</h2>
          <p className="text-xs text-slate-500">Apply a discount or promotional code to your account</p>
        </div>
      </div>

      {alreadyApplied ? (
        <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-lg p-4">
          <SafeIcon icon={FiIcons.FiCheckCircle} className="text-emerald-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-emerald-800">Voucher Already Applied</p>
            <p className="text-xs text-emerald-700 mt-0.5">
              Code <span className="font-bold">{user.applied_voucher}</span> is active on your account.
              Only one voucher can be applied per account.
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="flex gap-3">
            <div className="relative flex-1">
              <SafeIcon icon={FiIcons.FiTag} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Enter voucher code (e.g. LEGACY50)"
                className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-navy focus:border-brand-navy outline-none text-sm uppercase tracking-wide placeholder:normal-case placeholder:tracking-normal disabled:bg-slate-50 disabled:text-slate-400"
                value={code}
                onChange={(e) => { setCode(e.target.value.toUpperCase()); setResult(null); }}
                onKeyDown={(e) => e.key === 'Enter' && !loading && handleApply()}
                disabled={loading}
              />
            </div>
            <button
              onClick={handleApply}
              disabled={loading || !code.trim()}
              className="btn-primary px-5 flex-shrink-0 min-w-[90px]"
            >
              {loading ? (
                <ButtonSpinner />
              ) : (
                <><SafeIcon icon={FiIcons.FiZap} /> Apply</>
              )}
            </button>
          </div>

          {result?.type === 'error' && (
            <div className="mt-3 flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-lg p-3">
              <SafeIcon icon={FiIcons.FiXCircle} className="text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{result.message}</p>
            </div>
          )}
          {result?.type === 'success' && (
            <div className="mt-3 flex items-start gap-2.5 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <SafeIcon icon={FiIcons.FiCheckCircle} className="text-emerald-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-emerald-800">Voucher applied successfully!</p>
                <p className="text-xs text-emerald-700 mt-0.5">
                  {DISCOUNT_DESCRIPTIONS[result.voucher.discount_type]?.(result.voucher)}
                </p>
              </div>
            </div>
          )}
          <p className="text-xs text-slate-400 mt-3">
            Voucher codes are case-insensitive. One code per account.
          </p>
        </>
      )}
    </div>
  );
};